import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { fetchHomeContentData } from "../../utils/home.data";
import { useNavigate } from "react-router-dom";
import Logo from "../common/Logo";

const LandingPage = ({ className = "" }) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [homeContent, setHomeContent] = useState([]);
    const [imageLoaded, setImageLoaded] = useState(false);
    const intervalRef = useRef(null);
    const Navigate = useNavigate();

    useEffect(() => {
        const loadHomeContent = async () => {
            const content = await fetchHomeContentData();
            console.log(content)
            setHomeContent(content);
        };
        loadHomeContent();
    }, []);

    // Start auto-slide only after content is loaded
    useEffect(() => {
        if (homeContent.length === 0) return;

        intervalRef.current = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % homeContent.length);
        }, 2000);

        return () => clearInterval(intervalRef.current);
    }, [homeContent]);

    const currentSlide = homeContent[activeSlide] ?? {};

    return (
        <main
            className={`relative min-h-screen bg-[#e7e1d9]/90 overflow-hidden ${className}`}
            aria-label="InstaED Landing Page"
        >
            {/* ─── MOBILE LAYOUT ─── */}
            <div className="lg:hidden relative min-h-screen flex flex-col ">

                {/* Background image fills full screen on mobile */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/student.jpg"
                        alt=""
                        aria-hidden="true"
                        loading="eager"
                        onLoad={() => setImageLoaded(true)}
                        className={`w-full h-full object-cover transition-opacity duration-700 ${imageLoaded ? "opacity-100" : "opacity-0"
                            }`}
                    />
                    {/* Gradient overlay — strong at bottom for text legibility */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#e7e1d9]/70 via-[#e7e1d9]/80 to-[#e7e1d9]" />
                </div>

                {/* Logo — top-left on mobile */}
                <header className="relative z-10 pt-6 px-6">
                    <div className="flex flex-col items-start">
                        <div
                            className="bg-[#1d1d1d] rounded-xl px-5 py-3 inline-flex items-center justify-center shadow-md"
                            role="img"
                            aria-label="InstaED logo"
                        >
                            <span className="text-white text-xl italic font-srisakdi">Insta</span>
                            <span className="text-white text-4xl font-black leading-none">ED</span>
                        </div>
                        <p className="mt-2 text-[#2c2c2c] text-sm tracking-wide font-medium">
                            Online Tuitions | 6–12
                        </p>
                    </div>
                </header>

                {/* Text content — bottom of screen on mobile */}
                <div className="relative z-10 mt-[4px] px-6 pb-6 pt-10 w-auto flex flex-col justify-end text-center items-center min-h-full">
                    {/* Fixed-height container prevents layout shift on text change */}
                    <div className="min-h-[210px] flex flex-col justify-end">
                        <h1 className="m-0 leading-[1.05] tracking-[-0.5px]">
                            <span
                                key={`top-${activeSlide}`}
                                className="block text-[#6B5CE7] animate-fadeIn text-[1.7rem] sm:text-[2.2rem] lg:text-[2.6rem] font-light"
                            >
                                {currentSlide.titleTopLight}{" "}
                                <span className="font-black text-[2.1rem] sm:text-[2.8rem] lg:text-[3.1rem]">
                                    {currentSlide.titleTopBold}
                                </span>
                            </span>

                            <span
                                key={`bottom-${activeSlide}`}
                                className="block mt-1 text-[#6B5CE7] animate-fadeIn text-[1.5rem] sm:text-[1.9rem] lg:text-[2.2rem] font-light"
                            >
                                {currentSlide.titleBottomSmall}{" "}
                                <span className="font-black text-[1.9rem] sm:text-[2.5rem] lg:text-[2.9rem]">
                                    {currentSlide.titleBottomBig}
                                </span>
                            </span>
                        </h1>

                        <p
                            key={`desc-${activeSlide}`}
                            className="mt-4 text-[#343434] text-base leading-relaxed font-light animate-fadeIn"
                        >
                            {currentSlide.description}
                        </p>
                    </div>

                    {/* Dots */}
                    <div
                        className="flex items-center gap-3 mt-6 justify-center"
                        role="tablist"
                        aria-label="Slide indicators"
                    >
                        {homeContent.map((slide, index) => (
                            <button
                                key={slide.id}
                                type="button"
                                role="tab"
                                aria-label={`Slide ${index + 1}`}
                                aria-selected={activeSlide === index}
                                onClick={() => setActiveSlide(index)}
                                className={`h-3 w-3 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30 ${activeSlide === index
                                    ? "bg-[#2f2f2f] scale-125"
                                    : "bg-white border border-[#d4d4d4]"
                                    }`}
                            />
                        ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-8">
                        <button
                            onClick={() => { Navigate('/login') }}
                            type="button"
                            className="border border-[#a8a29b] bg-[#f6f3ef] hover:bg-white text-[#1d1d1d] px-9 py-3.5 rounded-md text-base font-medium transition-all duration-300 shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-black/20"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            </div>

            {/* ─── DESKTOP LAYOUT ─── */}
            <div className="hidden lg:flex min-h-screen items-center justify-center px-10 py-8">
                <section
                    aria-label="InstaED Hero Section"
                    className="w-full max-w-7xl flex flex-row items-center justify-between gap-16"
                >
                    {/* LEFT — Image */}
                    <div className="w-[46%] flex justify-center flex-shrink-0">
                        <div className="relative w-full max-w-[430px]">
                            <img
                                src="/student.jpg"
                                alt="Student attending an online tuition class with InstaED"
                                loading="eager"
                                onLoad={() => setImageLoaded(true)}
                                width={430}
                                height={560}
                                className={`w-full h-[560px] object-cover rounded-[28px] shadow-md transition-opacity duration-700 ${imageLoaded ? "opacity-100" : "opacity-0"
                                    }`}
                            />
                        </div>
                    </div>

                    {/* RIGHT — Content */}
                    <div className="w-[54%] flex flex-col items-start text-left">

                        <Logo />

                        <div className="mt-14 w-full max-w-[620px]">
                            <div
                                className="min-h-[190px]"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                <h1 className="m-0 leading-[1.05] tracking-[-0.5px]">
                                    <span
                                        key={`top-${activeSlide}`}
                                        className="block text-[#6B5CE7] animate-fadeIn text-[1.7rem] sm:text-[2.2rem] lg:text-[2.6rem] font-light"
                                    >
                                        {currentSlide.titleTopLight}{" "}
                                        <span className="font-black text-[2.1rem] sm:text-[2.8rem] lg:text-[3.1rem]">
                                            {currentSlide.titleTopBold}
                                        </span>
                                    </span>

                                    <span
                                        key={`bottom-${activeSlide}`}
                                        className="block mt-1 text-[#6B5CE7] animate-fadeIn text-[1.5rem] sm:text-[1.9rem] lg:text-[2.2rem] font-light"
                                    >
                                        {currentSlide.titleBottomSmall}{" "}
                                        <span className="font-black text-[1.9rem] sm:text-[2.5rem] lg:text-[2.9rem]">
                                            {currentSlide.titleBottomBig}
                                        </span>
                                    </span>
                                </h1>

                                <p
                                    key={`ddesc-${activeSlide}`}
                                    className="mt-8 text-[#343434] text-lg sm:text-xl leading-relaxed max-w-[520px] font-light animate-fadeIn"
                                >
                                    {currentSlide.description}
                                </p>
                            </div>

                            {/* Dots */}
                            <div
                                className="flex items-center gap-4 mt-10"
                                role="tablist"
                                aria-label="Hero Content Slider"
                            >
                                {homeContent.map((slide, index) => (
                                    <button
                                        key={slide.id}
                                        type="button"
                                        role="tab"
                                        aria-label={`Go to slide ${index + 1}`}
                                        aria-selected={activeSlide === index}
                                        onClick={() => setActiveSlide(index)}
                                        className={`h-4 w-4 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-black/20 ${activeSlide === index
                                            ? "bg-[#2f2f2f] scale-110"
                                            : "bg-white border border-[#d4d4d4]"
                                            }`}
                                    />
                                ))}
                            </div>

                            {/* CTA */}
                            <div className="mt-14">
                                <button
                                    onClick={() => { Navigate('/login') }}
                                    type="button"
                                    className="border border-[#a8a29b] bg-[#f6f3ef] hover:bg-white text-[#1d1d1d] px-10 py-4 rounded-md text-lg font-medium transition-all duration-300 shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-black/20"
                                >
                                    Get Started
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </div >

            {/* ─── FOOTER (both layouts) ─── */}
            <footer footer className="absolute bottom-4 left-0 right-0 flex justify-center items-center text-sm text-[#555] gap-3 px-4 flex-wrap z-10" >
                <span>© 2026 InstaED</span>
                <span aria-hidden="true">|</span>
                <button
                    className="hover:underline focus:outline-none focus:underline"
                    type="button"
                >
                    Terms of Use
                </button>
                <span aria-hidden="true">|</span>
                <button
                    className="hover:underline focus:outline-none focus:underline"
                    type="button"
                >
                    Privacy Policy
                </button>
            </footer >
        </main >
    );
};

LandingPage.propTypes = {
    className: PropTypes.string,
};

export default LandingPage;